import {
  ANALYTICS_MASK,
  EMBEDDED_URL_QUERY_KEYS,
  isOpaqueIdSegment,
  isSensitivePathname,
  SENSITIVE_PATH_PREFIXES,
  SENSITIVE_QUERY_KEYS,
  stripQueryFromEmbeddedUrl
} from '~/config/sensitiveRoutes';

export const isUrlAbsolute = (url = '') => {
  return (
    url.indexOf('://') > 0 ||
    url.startsWith('//') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:')
  );
};

// KVDBIL-7729: GA4's built-in redaction only scrubs query params and emails, not
// path segments, so sensitive URLs are masked here before reaching the dataLayer.

const PATH_SEGMENT_SEPARATOR = /(\/|%2[Ff])/;

export const sanitizeUrlForAnalytics = (url = ''): string => {
  if (!url) {
    return url;
  }

  const maskPath = (pathname: string) => {
    const masked = SENSITIVE_PATH_PREFIXES.reduce(
      (acc, prefix) =>
        acc.replace(new RegExp(`(/${prefix}/)[^/?#]+`), `$1${ANALYTICS_MASK}`),
      pathname
    );

    if (!isSensitivePathname(masked)) {
      return masked;
    }

    return masked
      .split(PATH_SEGMENT_SEPARATOR)
      .map(segment => (isOpaqueIdSegment(segment) ? ANALYTICS_MASK : segment))
      .join('');
  };

  try {
    // Absolute URLs: parse so we only touch path + query and preserve the rest.
    const parsed = new URL(url);
    parsed.pathname = maskPath(parsed.pathname);
    SENSITIVE_QUERY_KEYS.forEach(key => {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, ANALYTICS_MASK);
      }
    });
    EMBEDDED_URL_QUERY_KEYS.forEach(key => {
      const value = parsed.searchParams.get(key);
      if (value) {
        parsed.searchParams.set(key, stripQueryFromEmbeddedUrl(value));
      }
    });
    return parsed.toString();
  } catch {
    // Relative paths / unparseable input: mask with regex, leave the rest intact.
    const queryStart = url.search(/[?#]/);
    const pathname = queryStart === -1 ? url : url.slice(0, queryStart);
    let query = queryStart === -1 ? '' : url.slice(queryStart);

    SENSITIVE_QUERY_KEYS.forEach(key => {
      query = query.replace(
        new RegExp(`([?&]${key}=)[^&#]*`),
        `$1${ANALYTICS_MASK}`
      );
    });
    EMBEDDED_URL_QUERY_KEYS.forEach(key => {
      query = query.replace(
        new RegExp(`([?&]${key}=)([^&#]*)`),
        (_match, prefix: string, value: string) =>
          `${prefix}${encodeURIComponent(stripQueryFromEmbeddedUrl(value))}`
      );
    });

    return `${maskPath(pathname)}${query}`;
  }
};

export const getUrlSearchParams = (queryString?: string) => {
  if (!queryString) {
    return {};
  }

  const hasParams = queryString.includes('?');
  if (!hasParams) {
    return {};
  }

  const searchParams = new URLSearchParams(queryString.split('?')[1]);

  return Array.from(searchParams.entries()).reduce(
    (acc: Record<string, string>, [key, value]) => ({ ...acc, [key]: value }),
    {}
  );
};
